import moment from 'moment';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { actionsTypes } from '../../../../config';
import { createRoot } from 'react-dom/client';
import PositionDetail from '../../components/positionDetail';
import useChangeSymbol from '../../../../hooks/useChangeSymbol';
import { useAppDispatch } from '../../../../redux/app';

function PositionVirtualList({
  data,
  itemHeight = 50,
  containerHeight = 500,
  overscan = 3 // จำนวนรายการเพิ่มเติม ด้านบนล่าง
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);

  // คำนวณความสูงทั้งหมด
  const totalHeight = data.length * itemHeight;

  // ใช้ความสูงที่เหมาะสม (ไม่เกิน totalHeight)
  const actualContainerHeight = Math.min(containerHeight, totalHeight);

  // คำนวณ visible range พร้อม overscan
  const { startIndex, endIndex, offsetY } = useMemo(() => {
    const visibleStartIndex = Math.floor(scrollTop / itemHeight);
    const visibleEndIndex = Math.ceil((scrollTop + actualContainerHeight) / itemHeight);

    // เพิ่ม buffer ด้านบนล่าง
    const start = Math.max(0, visibleStartIndex - overscan);
    const end = Math.min(data.length, visibleEndIndex + overscan);
    const offset = start * itemHeight;

    return {
      startIndex: start,
      endIndex: end,
      offsetY: offset
    };
  }, [scrollTop, itemHeight, actualContainerHeight, data.length, overscan]);

  const visibleItems = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex]
  );

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      // id="dynamic-height-position"
      id="custom-order-scroll"
      style={{
        height: 'calc(100% - 410px)',
        overflow: totalHeight > actualContainerHeight ? 'auto' : 'hidden',
        position: 'relative'
      }}>
      <div
        style={{
          position: 'relative'
        }}>
        <div
          // id="custom-order-scroll"
          className="table-order-scroll history"
          style={{
            transform: `translateY(${offsetY}px)`,
            willChange: 'transform'
          }}>
          {visibleItems.map((item, index) => (
            <VirtualListItem
              key={startIndex + index}
              item={item}
              index={startIndex + index}
              height={itemHeight}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const renderAction = (x, e) => {
  switch (x) {
    case 2:
      return <div className="font-bold text-[#E5E5E5]">Balance</div>;
    case 3:
      return <div className="font-bold text-[#E5E5E5]">Credit</div>;
    default:
      return (
        <div
          style={{ color: actionsTypes[x]?.color, marginLeft: '8px' }}
          className="font-bold text-sm">
          {actionsTypes[x]?.name}
          <span className="ml-2">{e.volume / 10000}</span>
        </div>
      );
  }
};
const renderDescription = (x, value) => {
  switch (x) {
    case 3:
      return value > 0 ? 'Credit In' : value < 0 ? 'Credit Out' : '';
    case 19:
      return 'Negative Balance Protection';
    default:
      return value > 0 ? 'Deposit' : value < 0 ? 'Withdrawal' : '';
  }
};

const renderPositionDetail = (e, openChart) => {
  const panel = document.getElementById('history-panel');
  if (panel) {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.top = '0';
    el.style.left = '0';
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.zIndex = '99';

    const handleBack = () => {
      panel.removeChild(el);
    };

    panel.appendChild(el);
    createRoot(el).render(<PositionDetail onBack={handleBack} data={e} openChart={openChart} />);
    return el;
  }
  return null;
};

// แยก Component เพื่อ optimize การ render
export const VirtualListItem = React.memo(({ item, index, height }) => {
  const dispatch = useAppDispatch();
  const changeSymbol = useChangeSymbol(dispatch);

  return (
    <div
      onClick={() => renderPositionDetail(item, changeSymbol)}
      key={index}
      id={index + 1}
      className="table-body hover:bg-[#404040] h-[auto]"
      style={{
        padding: '8px',
        margin: '0px 16px',
        borderRadius: '8px',
        transformOrigin: 'top'
      }}>
      <div className="flex h-full items-center">
        <div className="flex w-full">
          <div className="w-[32px] content-center">
            {item.action !== 2 && item.action !== 3 ? (
              <img
                src={`https://storage.googleapis.com/iux-storage/symbol/${item.symbol}.png`}
                alt="..."
                onError={({ currentTarget }) => {
                  currentTarget.src = 'globe.svg';
                }}
                className="w-full rounded-[50%]"
              />
            ) : (
              <img
                src={`/logo_mini.png`}
                alt="..."
                onError={({ currentTarget }) => {
                  currentTarget.src = 'globe.svg';
                }}
                className="w-[32px] h-[32px] rounded-[50%] bg-[#5F5F5F]"
              />
            )}
          </div>
          <div className="ml-3 flex flex-col gap-1">
            <div className="flex items-baseline text-sm">
              <div className="text-[#e5e5e5] font-bold">{item.symbol}</div>
              {renderAction(item.action, item)}
            </div>
            <div className="flex text-[#e5e5e5]">
              <div style={{ color: '#9f9f9f' }} className="flex items-center">
                {item.openPrice && item.openPrice ? (
                  <>
                    <div className="text-xs">{item.openPrice?.toFixed(item.digits)}</div>
                    <svg
                      className="mx-2"
                      width="10"
                      height="6"
                      viewBox="0 0 10 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M9.88543 2.72349C9.88531 2.72338 9.88521 2.72324 9.88508 2.72312L7.84398 0.691874C7.69107 0.539706 7.44375 0.540272 7.29154 0.693202C7.13936 0.846112 7.13994 1.09344 7.29285 1.24562L8.66324 2.60937H0.390625C0.174883 2.60937 0 2.78426 0 3C0 3.21574 0.174883 3.39062 0.390625 3.39062H8.66322L7.29287 4.75437C7.13996 4.90656 7.13938 5.15389 7.29156 5.3068C7.44377 5.45974 7.69111 5.46027 7.844 5.30812L9.8851 3.27687C9.88521 3.27676 9.88531 3.27662 9.88545 3.2765C10.0384 3.12381 10.0379 2.87568 9.88543 2.72349Z"
                        fill="#9f9f9f"
                      />
                    </svg>
                    <div className="text-xs">{item.closePrice?.toFixed(item.digits)}</div>
                  </>
                ) : (
                  <div className="text-xs text-[#9f9f9f] max-w-[243px]">
                    {renderDescription(item.action, item.profit)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div
            className="text-end text-sm font-bold"
            style={{
              color: item?.profit < 0 ? '#EF4533' : '#28AFFF'
            }}>
            {item.profit?.toFixed(2)}
          </div>
          <div className="text-[#9f9f9f] text-xs whitespace-pre">
            {moment.unix(item.closeTime).utc().format('YYYY.MM.DD HH:mm:ss')}
          </div>
        </div>
      </div>
    </div>
  );
});

VirtualListItem.displayName = 'VirtualListItem';

export default PositionVirtualList;
